import { useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Star, Trash2, RefreshCw, Type, Sparkles, Package } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_IMAGES = 5;

export const ProductImageManager = ({ product, onUpdated }) => {
  const { API } = useAuth();
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [replacingImageId, setReplacingImageId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyImageId, setBusyImageId] = useState(null);
  const [error, setError] = useState("");
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [editingAltId, setEditingAltId] = useState(null);
  const [altDraft, setAltDraft] = useState("");
  const images = product.images || [];

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) return "Please choose a PNG, JPG or WEBP image.";
    if (file.size > MAX_BYTES) return "Image must be 10MB or smaller.";
    return null;
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setError("");
    const remainingSlots = MAX_IMAGES - images.length;
    const toUpload = files.slice(0, remainingSlots);
    const skippedCount = files.length - toUpload.length;
    setUploading(true);
    const errors = [];
    for (const file of toUpload) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await axios.post(`${API}/org/products/${product.product_id}/images`, formData, { withCredentials: true });
        onUpdated(data.product);
      } catch (err) {
        errors.push(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
    if (skippedCount > 0) {
      errors.push(`${skippedCount} file${skippedCount === 1 ? "" : "s"} skipped — only ${MAX_IMAGES} images allowed.`);
    }
    if (errors.length > 0) setError(errors.join(" "));
    setUploading(false);
  };

  const startReplace = (imageId) => {
    setReplacingImageId(imageId);
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const imageId = replacingImageId;
    setReplacingImageId(null);
    if (!file || !imageId) return;
    setError("");
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusyImageId(imageId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.patch(`${API}/org/products/${product.product_id}/images/${imageId}`, formData, { withCredentials: true });
      onUpdated(data.product);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyImageId(null);
    }
  };

  const removeImage = async (imageId) => {
    setBusyImageId(imageId);
    setError("");
    try {
      const { data } = await axios.delete(`${API}/org/products/${product.product_id}/images/${imageId}`, { withCredentials: true });
      onUpdated(data.product);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyImageId(null);
    }
  };

  const setPrimary = async (imageId) => {
    setBusyImageId(imageId);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/org/products/${product.product_id}/images/${imageId}/primary`, {}, { withCredentials: true });
      onUpdated(data.product);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyImageId(null);
    }
  };

  const handleDrop = async (targetId) => {
    const sourceId = draggedId;
    setDraggedId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const ids = images.map((img) => img.image_id);
    const fromIdx = ids.indexOf(sourceId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, sourceId);
    setBusyImageId(sourceId);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/org/products/${product.product_id}/images/reorder`, { image_ids: ids }, { withCredentials: true });
      onUpdated(data.product);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyImageId(null);
    }
  };

  const startAltEdit = (img) => {
    setEditingAltId(img.image_id);
    setAltDraft(img.alt_text || "");
  };

  const saveAltText = async () => {
    const imageId = editingAltId;
    setBusyImageId(imageId);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/org/products/${product.product_id}/images/${imageId}/alt-text`, { alt_text: altDraft.trim() }, { withCredentials: true });
      onUpdated(data.product);
      setEditingAltId(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyImageId(null);
    }
  };

  return (
    <div data-testid={`org-product-images-${product.product_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        {images.map((img) => (
          <div
            key={img.image_id}
            draggable
            onDragStart={() => setDraggedId(img.image_id)}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragOverId !== img.image_id) setDragOverId(img.image_id);
            }}
            onDragLeave={() => setDragOverId((cur) => (cur === img.image_id ? null : cur))}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(img.image_id);
            }}
            onDragEnd={() => {
              setDraggedId(null);
              setDragOverId(null);
            }}
            className={`group relative h-16 w-16 shrink-0 cursor-grab overflow-hidden rounded-xl border transition ${
              dragOverId === img.image_id && draggedId !== img.image_id
                ? "border-[#0052FF] ring-2 ring-[#0052FF]"
                : "border-slate-200 dark:border-white/15"
            }`}
            data-testid={`org-product-image-${img.image_id}`}
          >
            <img src={`${BACKEND_URL}${img.url}`} alt={img.alt_text || product.name} className="h-full w-full object-cover" />
            {img.is_primary && <Star size={12} className="absolute left-1 top-1 fill-amber-400 text-amber-400" data-testid={`org-product-image-primary-badge-${img.image_id}`} />}
            {img.source === "ai_generated" && (
              <span className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-violet-600 px-1.5 py-0.5 text-[9px] font-bold text-white" data-testid={`org-product-image-ai-badge-${img.image_id}`}>
                <Sparkles size={8} /> AI
              </span>
            )}
            <div className="absolute inset-0 hidden items-center justify-center gap-1 bg-slate-950/60 group-hover:flex">
              {!img.is_primary && (
                <button type="button" onClick={() => setPrimary(img.image_id)} disabled={busyImageId === img.image_id} data-testid={`org-product-set-primary-${img.image_id}`} className="rounded-full bg-white/90 p-1.5 text-slate-700">
                  <Star size={12} />
                </button>
              )}
              <button type="button" onClick={() => startReplace(img.image_id)} disabled={busyImageId === img.image_id} data-testid={`org-product-replace-image-${img.image_id}`} className="rounded-full bg-white/90 p-1.5 text-slate-700">
                <RefreshCw size={12} />
              </button>
              <button type="button" onClick={() => startAltEdit(img)} disabled={busyImageId === img.image_id} data-testid={`org-product-edit-alt-${img.image_id}`} className="rounded-full bg-white/90 p-1.5 text-slate-700">
                <Type size={12} />
              </button>
              <button type="button" onClick={() => removeImage(img.image_id)} disabled={busyImageId === img.image_id} data-testid={`org-product-remove-image-${img.image_id}`} className="rounded-full bg-white/90 p-1.5 text-red-600">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
        {images.length === 0 && (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-300 dark:border-white/15 dark:text-slate-600">
            <Package size={20} />
          </div>
        )}
        {images.length < MAX_IMAGES && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`org-product-add-image-${product.product_id}`}
            className="flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 text-slate-400 hover:border-[#0052FF] hover:text-[#0052FF] disabled:opacity-50 dark:border-white/20 dark:text-slate-500"
          >
            <ImagePlus size={16} />
            <span className="text-[10px] font-bold">{uploading ? "…" : "Add"}</span>
          </button>
        )}
        <input ref={fileInputRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} data-testid={`org-product-image-input-${product.product_id}`} />
        <input ref={replaceInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleReplaceFile} data-testid={`org-product-image-replace-input-${product.product_id}`} />
      </div>
      {images.length > 1 && <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">Drag images to reorder.</p>}
      {editingAltId && (
        <div className="mt-2 flex items-center gap-2" data-testid={`org-product-alt-text-editor-${editingAltId}`}>
          <input
            type="text"
            value={altDraft}
            onChange={(e) => setAltDraft(e.target.value)}
            maxLength={200}
            placeholder="Describe this image (for accessibility)"
            data-testid={`org-product-alt-text-input-${editingAltId}`}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 focus:border-[#0052FF] focus:outline-none dark:border-white/15 dark:bg-slate-900 dark:text-slate-200"
          />
          <button type="button" onClick={saveAltText} disabled={busyImageId === editingAltId} data-testid={`org-product-alt-text-save-${editingAltId}`} className="rounded-lg bg-[#0052FF] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
            Save
          </button>
          <button type="button" onClick={() => setEditingAltId(null)} data-testid={`org-product-alt-text-cancel-${editingAltId}`} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500">
            Cancel
          </button>
        </div>
      )}
      <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">{images.length} of {MAX_IMAGES} images · PNG, JPG or WEBP · Up to 10MB</p>
      {error && <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400" data-testid={`org-product-image-error-${product.product_id}`}>{error}</p>}
    </div>
  );
};
